import React, { useState, useMemo } from 'react';
import { MDBRow, MDBCol } from 'mdbreact';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { renderToast } from 'components/Informations/Toast/utils';

import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import If from 'components/If';
import Page from 'components/PageStructure';

import DateField from 'components/FormGroup/DateField';
import FilledButton from 'components/Buttons/FilledButton';

import history from 'utils/history';
import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';
import { monthYear, totallyHours } from 'utils/moment';

import InformationCard from 'components/Cards/InformationCard';
import AppointmentForm from './InternalComponents/AppointmentForm';
import AppointmentHistory from './InternalComponents/AppointmentHistory';
import AppointmentsTable from './InternalComponents/AppointmentsTable';

import { renderOverlay } from './utils';
import * as S from './styles';

const { getAppointments } = endpoints;

export default function Appointments({ user, location }) {
  const [currentMonth, setCurrentMonth] = useState(new Date().getTime());
  const [observer, setObserver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState({});
  const [currentTableRow, setCurrentTableRow] = useState(null);

  const [historyVisibility, setHistoryVisibility] = useState(false);
  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    closeTime: '',
  });
  const [toastVisibility, setToastVisibility] = useState(false);

  const { id_funcionario: idFuncionario } = user;
  const { date, totalHorasPonto, horasPonto } = currentAppointment;
  const { state } = location;

  const [allAppointments, allAppointmentsLoading] = useFetch(
    `${getAppointments}${
      state?.id_funcionario
        ? `/admin/${state?.id_funcionario}`
        : `/${idFuncionario}`
    }/${monthYear(currentMonth)}`,
    observer,
  );

  const handleMonthChange = event => {
    setCurrentAppointment({});
    setCurrentMonth(event);
  };

  const handleCurrentAppointmentChange = data => setCurrentAppointment(data);

  const resetAllSelections = () => [
    setCurrentAppointment({}),
    setCurrentTableRow(null),
  ];

  const monthlyAppointmentsCounter = useMemo(() => {
    if (allAppointmentsLoading) return '00:00';

    const { resumoDoMes } = JSON.parse(allAppointments);
    const pendingAppointments = resumoDoMes.filter(
      ({ horasEmProjeto }) => !!horasEmProjeto,
    );

    return (
      totallyHours(_.sumBy(pendingAppointments, 'totalHorasPonto')) || '00:00'
    );
  }, [allAppointments, allAppointmentsLoading]);

  return (
    <>
      <Page
        title="Apontamento de horas"
        subtitle={
          state
            ? 'Aponte as horas trabalhadas em cada um dos projetos'
            : 'Aponte as horas efetivas de trabalho em cada um dos projetos que você está alocado.'
        }
        previousPage={state ? '/auth/appointments/admin' : false}
        pageAction={() => history.goBack()}
      >
        <If condition={!!state}>
          <MDBRow>
            <MDBCol lg="3" sm="12">
              <InformationCard
                counter={monthlyAppointmentsCounter}
                description="Horas não apontadas"
              />
            </MDBCol>
          </MDBRow>
        </If>

        <MDBRow>
          <MDBCol lg="8" sm="12">
            <S.Container>
              <DateField
                onlyMonth
                labelText="Visualizando o mês:"
                selectedValue={currentMonth}
                handleChange={handleMonthChange}
                width={50}
              />
              <If condition={!state}>
                <FilledButton
                  text="Meus reembolsos"
                  handleClick={() => {
                    history.push('/auth/appointments/refunds-history');
                  }}
                />
              </If>
            </S.Container>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol lg="8" md="12">
            {!allAppointmentsLoading && (
              <AppointmentsTable
                data={JSON.parse(allAppointments)}
                selectAppointment={handleCurrentAppointmentChange}
                currentTableRow={currentTableRow}
                setCurrentTableRow={setCurrentTableRow}
                resetSelections={resetAllSelections}
                setToastVisibility={setToastVisibility}
                setToastInfos={setToastInfos}
                reloadAppointments={() => setObserver(!observer)}
                setLoading={setLoading}
                user={user}
                selectedEmployee={state?.id_funcionario ?? null}
                isAdminVisibility={!!state}
              />
            )}
          </MDBCol>

          <MDBCol lg="4" md="12">
            <AppointmentForm
              reloadAppointments={setObserver}
              resetSelections={resetAllSelections}
              currentAppointment={currentAppointment}
              user={user}
              setLoading={setLoading}
              setToastVisibility={setToastVisibility}
              setToastInfos={setToastInfos}
              setOverlayVisibility={setOverlayVisibility}
              setHistoryVisibility={setHistoryVisibility}
              state={state}
            />
          </MDBCol>
        </MDBRow>
      </Page>

      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.closeTime,
          setToastVisibility,
        )}

      {(allAppointmentsLoading || loading) && <ContentLoader isFull />}

      {historyVisibility && (
        <AppointmentHistory
          date={date}
          workedHours={totalHorasPonto}
          allHours={horasPonto}
          isVisible={historyVisibility}
          setIsVisible={setHistoryVisibility}
        />
      )}

      {overlayVisibility &&
        renderOverlay(
          overlayVisibility,
          setOverlayVisibility,
          resetAllSelections,
        )}
    </>
  );
}

Appointments.defaultProps = {
  user: {},
  location: {},
};

Appointments.propTypes = {
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};
